import { motion } from "framer-motion";
import { useMemo } from "react";

interface AuroraBackgroundProps {
  themeColor: "pink" | "red" | "purple" | "roseGold";
  style: "aurora" | "particles" | "stars" | "neonGrid";
  intensity: "subtle" | "normal" | "chaos";
}

const AuroraBackground = ({ themeColor, style, intensity }: AuroraBackgroundProps) => {
  const colorPalettes = {
    pink: {
      primary: "rgba(236, 72, 153, 0.4)",
      secondary: "rgba(244, 114, 182, 0.3)",
      tertiary: "rgba(251, 207, 232, 0.2)",
    },
    red: {
      primary: "rgba(239, 68, 68, 0.4)",
      secondary: "rgba(248, 113, 113, 0.3)",
      tertiary: "rgba(254, 202, 202, 0.2)",
    },
    purple: {
      primary: "rgba(168, 85, 247, 0.4)",
      secondary: "rgba(192, 132, 252, 0.3)",
      tertiary: "rgba(233, 213, 255, 0.2)",
    },
    roseGold: {
      primary: "rgba(251, 146, 60, 0.4)",
      secondary: "rgba(251, 191, 36, 0.3)",
      tertiary: "rgba(254, 243, 199, 0.2)",
    },
  };

  const colors = colorPalettes[themeColor];

  const animationSpeed = {
    subtle: 20,
    normal: 12,
    chaos: 6,
  };

  const stars = useMemo(() => 
    Array.from({ length: intensity === "chaos" ? 100 : intensity === "normal" ? 50 : 25 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 1 + Math.random() * 2,
      delay: Math.random() * 3,
      duration: 2 + Math.random() * 2,
    }))
  , [intensity]);

  const gridLines = useMemo(() => ({
    horizontal: Array.from({ length: 20 }, (_, i) => i * 5),
    vertical: Array.from({ length: 20 }, (_, i) => i * 5),
  }), []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {/* Base gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />

      {/* Aurora Effect */}
      {style === "aurora" && (
        <>
          {/* Aurora Layer 1 */}
          <motion.div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 80% 50% at 50% 0%, ${colors.primary}, transparent 70%)`,
            }}
            animate={{
              opacity: [0.5, 0.8, 0.5],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: animationSpeed[intensity],
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          
          {/* Aurora Layer 2 */}
          <motion.div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 60% 40% at 30% 20%, ${colors.secondary}, transparent 60%)`,
            }}
            animate={{
              opacity: [0.3, 0.6, 0.3],
              x: ["-10%", "10%", "-10%"],
            }}
            transition={{
              duration: animationSpeed[intensity] * 1.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* Aurora Layer 3 */}
          <motion.div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 70% 30% at 70% 80%, ${colors.tertiary}, transparent 50%)`,
            }}
            animate={{
              opacity: [0.4, 0.7, 0.4],
              y: ["-5%", "5%", "-5%"],
            }}
            transition={{
              duration: animationSpeed[intensity] * 0.8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* Shimmer overlay */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
            animate={{
              x: ["-100%", "100%"],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        </>
      )}

      {/* Stars Effect */}
      {style === "stars" && (
        <>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black" />
          {stars.map((star) => (
            <motion.div
              key={star.id}
              className="absolute rounded-full bg-white"
              style={{
                left: `${star.x}%`,
                top: `${star.y}%`,
                width: star.size,
                height: star.size,
              }}
              animate={{
                opacity: [0.3, 1, 0.3],
                scale: [1, 1.5, 1],
              }}
              transition={{
                duration: star.duration,
                delay: star.delay,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
          {/* Shooting stars */}
          {intensity !== "subtle" && (
            <>
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={`shooting-${i}`}
                  className="absolute w-1 h-1 bg-white rounded-full"
                  style={{
                    boxShadow: "0 0 6px 2px rgba(255,255,255,0.8), -20px 0 10px rgba(255,255,255,0.3)",
                  }}
                  initial={{ x: "-10%", y: `${20 + i * 30}%`, opacity: 0 }}
                  animate={{
                    x: ["120%"],
                    y: [`${40 + i * 20}%`],
                    opacity: [0, 1, 1, 0],
                  }}
                  transition={{
                    duration: 2,
                    delay: i * 4 + Math.random() * 2,
                    repeat: Infinity,
                    repeatDelay: 8 + Math.random() * 4,
                    ease: "easeOut",
                  }}
                />
              ))}
            </>
          )}
        </>
      )}

      {/* Neon Grid Effect */}
      {style === "neonGrid" && (
        <>
          <div className="absolute inset-0 bg-slate-950" />
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `
                linear-gradient(${colors.primary} 1px, transparent 1px),
                linear-gradient(90deg, ${colors.primary} 1px, transparent 1px)
              `,
              backgroundSize: "50px 50px",
            }}
          />
          <motion.div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(circle at 50% 50%, ${colors.secondary}, transparent 70%)`,
            }}
            animate={{
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: animationSpeed[intensity],
              repeat: Infinity,
            }}
          />
          {/* Scan line */}
          <motion.div
            className="absolute w-full h-0.5 left-0"
            style={{
              background: `linear-gradient(90deg, transparent, ${colors.primary}, transparent)`,
              boxShadow: `0 0 20px 5px ${colors.primary}`,
            }}
            animate={{
              top: ["0%", "100%"],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        </>
      )}

      {/* Particles Effect (enhanced) */}
      {style === "particles" && (
        <>
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
          <motion.div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse at center, ${colors.primary}, transparent 70%)`,
            }}
            animate={{
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration: animationSpeed[intensity],
              repeat: Infinity,
            }}
          />
        </>
      )}

      {/* Noise/grain texture overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Vignette */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_rgba(0,0,0,0.4)_100%)]" />
    </div>
  );
};

export default AuroraBackground;
